import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type {AppLocale} from './locales'
import {DEFAULT_LOCALE} from './locales'
import bg from './messages/bg.json'
import en from './messages/en.json'
import tr from './messages/tr.json'
import de from './messages/de.json'
import sv from './messages/sv.json'

const STORAGE_OVERRIDES = 'eventatv_i18n_overrides_v1'

const baseBundles: Record<AppLocale, Record<string, string>> = {
  bg: bg as Record<string, string>,
  en: en as Record<string, string>,
  tr: tr as Record<string, string>,
  de: de as Record<string, string>,
  sv: sv as Record<string, string>,
}

export type LocaleOverrides = Partial<Record<AppLocale, Record<string, string>>>

function readOverrides(): LocaleOverrides {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_OVERRIDES)
    if (!raw) return {}
    return JSON.parse(raw) as LocaleOverrides
  } catch {
    return {}
  }
}

function writeOverrides(next: LocaleOverrides) {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_OVERRIDES, JSON.stringify(next))
}

export interface I18nContextValue {
  locale: AppLocale
  t: (key: string) => string
  /** String for a given `locale` bundle (e.g. a CTA label in the *target* site language). */
  tInLocale: (targetLocale: AppLocale, key: string) => string
  /** All known keys (from Bulgarian bundle). */
  translationKeys: string[]
  overrides: LocaleOverrides
  setOverride: (locale: AppLocale, key: string, value: string) => void
  resetLocaleOverrides: (locale: AppLocale) => void
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

export function I18nProvider(props: {
  locale: AppLocale
  children: React.ReactNode
}) {
  const {locale} = props
  const [overrides, setOverridesState] = useState<LocaleOverrides>(() =>
    readOverrides(),
  )

  const merged = useMemo(() => {
    const base = baseBundles[locale] || baseBundles[DEFAULT_LOCALE]
    const extra = overrides[locale] || {}
    return {...base, ...extra}
  }, [locale, overrides])

  const pickString = (
    bundle: Record<string, string>,
    key: string,
  ): string | undefined => {
    const v = bundle[key]
    return v !== undefined && v !== '' ? v : undefined
  }

  const t = useCallback(
    (key: string) => {
      const primary = pickString(merged, key)
      if (primary !== undefined) return primary
      const enV = pickString(baseBundles.en, key)
      if (enV !== undefined) return enV
      const bgV = pickString(baseBundles.bg, key)
      if (bgV !== undefined) return bgV
      return key
    },
    [merged],
  )

  const tInLocale = useCallback(
    (targetLocale: AppLocale, key: string) => {
      const base = baseBundles[targetLocale] || baseBundles[DEFAULT_LOCALE]
      const extra = overrides[targetLocale] || {}
      const mergedForTarget = {...base, ...extra}
      const primary = pickString(mergedForTarget, key)
      if (primary !== undefined) return primary
      const enV = pickString(baseBundles.en, key)
      if (enV !== undefined) return enV
      const bgV = pickString(baseBundles.bg, key)
      if (bgV !== undefined) return bgV
      return key
    },
    [overrides],
  )

  const translationKeys = useMemo(() => Object.keys(baseBundles.bg).sort(), [])

  const setOverride = useCallback(
    (loc: AppLocale, key: string, value: string) => {
      setOverridesState((prev) => {
        const next: LocaleOverrides = {
          ...prev,
          [loc]: {...(prev[loc] || {}), [key]: value},
        }
        writeOverrides(next)
        return next
      })
    },
    [],
  )

  const resetLocaleOverrides = useCallback((loc: AppLocale) => {
    setOverridesState((prev) => {
      const next = {...prev, [loc]: {}}
      writeOverrides(next)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({
      locale,
      t,
      tInLocale,
      translationKeys,
      overrides,
      setOverride,
      resetLocaleOverrides,
    }),
    [
      locale,
      t,
      tInLocale,
      translationKeys,
      overrides,
      setOverride,
      resetLocaleOverrides,
    ],
  )

  return (
    <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider')
  }
  return ctx
}

export function useOptionalI18n(): I18nContextValue | undefined {
  return useContext(I18nContext)
}
